// 클라이언트 개인 설정 (localStorage).
//   ⚙️ 2026-09-07 기기 간 동기화 — 계정 단위로 같아도 되는 설정(테마·전송키·멘션·알림 상세·표시 토글)은 setter 가
//   markPrefChanged(키) 를 불러 서버 사본(prefsSync.ts)과 맞춘다. 기기 특성(화면 배치·다운로드 폴더)은 부르지 않는다.
//   🔴 이 파일은 import 가 없어야 한다 — scripts/check-send-key.mjs 가 node 로 직접 읽는다(zustand 등 브라우저 의존을 끌면 깨짐).
//      그래서 동기화 쪽은 훅으로 꽂는다: prefsSync.installPrefsSync → setPrefChangeHook.
type PrefChangeHook = (key: string) => void;
let prefChangeHook: PrefChangeHook | null = null;
export function setPrefChangeHook(fn: PrefChangeHook | null): void { prefChangeHook = fn; }
/** setter 가 값을 쓴 직후 호출 — 동기화 훅이 등록돼 있으면(앱 부팅 후) 그 기기의 변경 시각을 찍고 서버에 올린다. */
export function markPrefChanged(key: string): void { try { prefChangeHook?.(key); } catch { /* 동기화 실패가 설정 저장을 막지 않게 */ } }

const MENTION_SUFFIX_KEY = 'tpt-mention-suffix';

// 멘션 자동 텍스트 — @홍길동 선택 시 뒤에 자동 삽입 (예: "프로님~", "님 안녕하세요").
export function getMentionSuffix(): string {
	try { return localStorage.getItem(MENTION_SUFFIX_KEY) || ''; } catch { return ''; }
}

export function setMentionSuffix(v: string): void {
	try {
		if (v) localStorage.setItem(MENTION_SUFFIX_KEY, v);
		else localStorage.removeItem(MENTION_SUFFIX_KEY);
	} catch { /* private mode */ }
	markPrefChanged(MENTION_SUFFIX_KEY);
	window.dispatchEvent(new CustomEvent('tpt-mention-suffix-change', { detail: { suffix: v } }));
}

// ── 멘션 '직급+님' 자동 (2026-07-13) — 체크 시 상대 직급을 조회해 "@홍길동 프로님 " 처럼 자동 삽입.
//    (이름 뒤 공백은 멘션 인식 규칙 — 붙여 쓰면 RC 가 멘션으로 못 알아봄). 켜면 위 텍스트 suffix 보다 우선.
const MENTION_RANK_KEY = 'tpt-mention-rank-nim';
export function getMentionRankNim(): boolean {
	try { return localStorage.getItem(MENTION_RANK_KEY) === '1'; } catch { return false; }
}
export function setMentionRankNim(on: boolean): void {
	try {
		if (on) localStorage.setItem(MENTION_RANK_KEY, '1');
		else localStorage.removeItem(MENTION_RANK_KEY);
	} catch { /* private mode */ }
	markPrefChanged(MENTION_RANK_KEY);
}

// ── 🖥 화면 배치 (2026-08-26 사용자: 포터블 모니터를 세로로 세우면 1080×1920 이 되어 UI 가 온전히 안 나온다.
//    "자동 전환이 아니라 사용자가 골라 전환하는 게 오류가 없을 것") ──────────────────────────
//    'auto'   창 크기로 판정 (기본 — 종전 동작)
//    'wide'   좌우 분할 고정 (사이드바 + 대화 + 우측 패널)
//    'narrow' 한 화면씩 고정 (모바일과 같은 배치 — 세로 모니터·좁은 창에 적합)
//    기기 특성이라 기기별 저장(localStorage) — 계정에 동기화하지 않는다. 같은 사람이 데스크톱은 넓게,
//    세로 모니터 노트북은 좁게 쓰는 것이 정상이기 때문.
const LAYOUT_KEY = 'tpt-layout-mode';
export type LayoutMode = 'auto' | 'wide' | 'narrow';
export const LAYOUT_CHANGE_EVENT = 'tpt:layout-change';

export function getLayoutMode(): LayoutMode {
	try {
		const v = localStorage.getItem(LAYOUT_KEY);
		return v === 'wide' || v === 'narrow' ? v : 'auto';
	} catch { return 'auto'; }
}
// 📌 배치 고정 (2026-08-26 사용자 "폭이 줄어들면 대화창만 남는 게 자동으로 안 되게") —
//    켜면 창 폭 변화에 따른 **패널 자동 접기/펴기**를 하지 않는다(좌 사이드바 1024px·우측 패널 900px 임계).
//    사용자가 손으로 접고 편 상태만 남는다. 좁은 배치 전환(LayoutMode)과는 다른 축 — 그쪽은 '한 화면씩'
//    으로 갈아타는 것이고, 이건 넓은 배치 안에서 패널이 멋대로 사라지지 않게 하는 것.
const LAYOUT_FIXED_KEY = 'tpt-layout-fixed';

export function getLayoutFixed(): boolean {
	try { return localStorage.getItem(LAYOUT_FIXED_KEY) === '1'; } catch { return false; }
}
export function setLayoutFixed(on: boolean): void {
	try {
		if (on) localStorage.setItem(LAYOUT_FIXED_KEY, '1');
		else localStorage.removeItem(LAYOUT_FIXED_KEY);
	} catch { /* private mode */ }
	try { window.dispatchEvent(new CustomEvent(LAYOUT_CHANGE_EVENT)); } catch { /* SSR */ }
}

export function setLayoutMode(m: LayoutMode): void {
	try {
		if (m === 'auto') localStorage.removeItem(LAYOUT_KEY);
		else localStorage.setItem(LAYOUT_KEY, m);
	} catch { /* private mode */ }
	// 열려 있는 화면이 즉시 따라오게 — useIsMobile 이 이 이벤트를 듣는다.
	try { window.dispatchEvent(new CustomEvent(LAYOUT_CHANGE_EVENT)); } catch { /* SSR */ }
}

// ── 사진 전송 화질 (2026-07-22) — 전송 시 이미지 치수를 원본의 n% 로 축소해 속도 개선.
//    기본 '50'(절반). 'original' = 무가공 원본. 실제 축소는 utils/imageCompress.ts 담당.
//    🏢 2026-08-13 사용자 요청으로 **개인 설정 → 관리자 전역 설정**으로 이관.
//       서버(/api/tpt/app-settings)가 정본이고, localStorage 는 오프라인·부팅 직후를 위한 캐시일 뿐이다.
//       개인이 바꾸는 UI 는 없앴다(setImageSendQuality 는 서버값 캐싱 전용).
//    🎚 2026-08-26 사용자 "25% 씩 건너뛰지 말고 글자 크기처럼 미세하게" — 4단 라디오(원본/75/50/25)를
//       10~100% 슬라이더로 바꿨다. 저장 표현은 그대로라 기존 값('75'/'50'/'25')은 마이그레이션 없이 유효하다.
//       100% = 치수는 그대로 두고 재인코딩만(용량은 줄어듦) — 무가공인 'original' 과는 다르다.
const IMAGE_SEND_QUALITY_KEY = 'tpt-image-send-quality';
export const IMG_QUALITY_MIN = 10;
export const IMG_QUALITY_MAX = 100;
export const IMG_QUALITY_STEP = 5;          // 슬라이더 한 칸 · ＋/− 버튼 한 번
export const IMG_QUALITY_DEFAULT = '50';
/** 'original'(무가공) 또는 축소 비율 정수 문자열('10'~'100'). 값 판별은 isImageQuality 로. */
export type ImageSendQuality = string;

export function isImageQuality(v: unknown): v is ImageSendQuality {
	if (v === 'original') return true;
	if (typeof v !== 'string' || !/^\d{1,3}$/.test(v)) return false;
	const n = Number(v);
	return n >= IMG_QUALITY_MIN && n <= IMG_QUALITY_MAX;
}
/** 축소 배율(0<x<=1). 'original' 은 null — 호출부는 무가공 전송으로 분기한다. */
export function imageQualityScale(q: ImageSendQuality): number | null {
	if (q === 'original') return null;
	const n = Number(q);
	if (!Number.isFinite(n)) return null;
	return Math.min(1, Math.max(IMG_QUALITY_MIN, n) / 100);
}
/** 슬라이더·＋/− 입력을 유효 범위 정수로 (범위 밖·NaN 은 기본값). */
export function clampImageQuality(n: number): number {
	if (!Number.isFinite(n)) return Number(IMG_QUALITY_DEFAULT);
	return Math.min(IMG_QUALITY_MAX, Math.max(IMG_QUALITY_MIN, Math.round(n)));
}

export function getImageSendQuality(): ImageSendQuality {
	try {
		const v = localStorage.getItem(IMAGE_SEND_QUALITY_KEY);
		if (isImageQuality(v)) return v;
	} catch { /* private mode */ }
	return IMG_QUALITY_DEFAULT;
}
export function setImageSendQuality(v: ImageSendQuality): void {
	try { localStorage.setItem(IMAGE_SEND_QUALITY_KEY, v); } catch { /* private mode */ }
}

/** 부팅 시 1회 — 관리자가 정한 전역 화질을 받아 캐시에 반영. 실패하면 기존 캐시 유지(전송은 계속 동작). */
export async function syncImageSendQuality(): Promise<void> {
	try {
		const r = await fetch('/api/tpt/app-settings');
		if (!r.ok) return;
		const j = await r.json() as { imgQuality?: string };
		if (isImageQuality(j.imgQuality)) setImageSendQuality(j.imgQuality);
	} catch { /* 오프라인 — 캐시 사용 */ }
}

// ── 알림 상세 설정 ──
const NOTIF_PREFS_KEY = 'tpt-notif-prefs';
export type NotifPreview = 'full' | 'sender' | 'none';
/** 🖥 데스크톱 알림 팝업 위치 (2026-09-04 원격 "알람의 표시위치를 지정… 기본은 우측하단") — desktop/notification.ts 가 좌표 계산에 쓴다 */
export type NotifDesktopPos = 'bottom-right' | 'bottom-left' | 'bottom-center' | 'top-right' | 'top-left' | 'top-center';
export const NOTIF_DESKTOP_POSITIONS: NotifDesktopPos[] = ['bottom-right', 'bottom-left', 'bottom-center', 'top-right', 'top-left', 'top-center'];
// 🔊 고를 수 있는 알림음 (2026-09-18 사용자가 docs/audio 로 전달한 mp3 + 기존 기본음).
//   id = public/sounds 파일명(확장자 제외)과 같다 — 새 음원을 넣을 때 파일을 public/sounds 에 두고 여기 한 줄만 더한다.
//   라벨은 로케일 키 settings.sound.<id>. 🔊 2026-09-18 부터 **안드로이드 백그라운드(FCM·폴링)도 이 선택을 따른다** —
//   NotifyService.msgChannelFor 가 음원마다 알림 채널을 따로 만든다(res/raw/<id> 필요, 채널 소리는 생성 후 불변).
//   새 음원을 추가할 때는 public/sounds 와 .ops/android-webview/app/src/main/res/raw 양쪽에 같은 이름으로 넣어야 한다.
export interface NotifSound { id: string; file: string }
export const NOTIF_SOUNDS: NotifSound[] = [
	{ id: 'messagealarm', file: 'messagealarm.wav' },   // 기본 (종전 음원)
	{ id: 'galaxy1', file: 'galaxy1.mp3' },
	{ id: 'galaxy2', file: 'galaxy2.mp3' },
	{ id: 'iphone1', file: 'iphone1.mp3' },
	{ id: 'alert2', file: 'alert2.mp3' },
	{ id: 'alert3', file: 'alert3.mp3' },
	{ id: 'duck', file: 'duck.mp3' },
	{ id: 'court', file: 'court.mp3' },
];
export const NOTIF_SOUND_DEFAULT = 'messagealarm';
/** 음원 id → 재생 URL. 모르는 id(구 설정·오타)는 기본음으로 — 소리가 안 나는 상태로 두지 않는다. */
export function notifSoundUrl(id?: string): string {
	const s = NOTIF_SOUNDS.find((x) => x.id === id) || NOTIF_SOUNDS[0];
	return `/sounds/${s.file}`;
}

export interface NotifPrefs {
	sound: boolean;              // 알림음 ON/OFF
	/** 🔊 알림음 종류 (2026-09-18) — NOTIF_SOUNDS 의 id. 없으면 기본음. */
	soundName: string;
	dndEnabled: boolean;         // 방해 금지 모드 사용
	dndStart: string;            // 'HH:MM'
	dndEnd: string;              // 'HH:MM'
	preview: NotifPreview;       // 알림 미리보기 단계
	desktopPos: NotifDesktopPos; // 데스크톱 팝업 위치 (Tauri 전용, 웹은 무시)
	/** 📢 @멘션 수신 시 화면 중앙 큰 알림 (2026-09-07 사용자 "옵션으로 선택, 기본은 화면중앙알람") — 끄면 토스트·소리만 */
	mentionCenter: boolean;
}
const NOTIF_DEFAULTS: NotifPrefs = { sound: true, soundName: NOTIF_SOUND_DEFAULT, dndEnabled: false, dndStart: '22:00', dndEnd: '08:00', preview: 'full', desktopPos: 'bottom-right', mentionCenter: true };

export function getNotifPrefs(): NotifPrefs {
	try {
		const raw = localStorage.getItem(NOTIF_PREFS_KEY);
		if (raw) return { ...NOTIF_DEFAULTS, ...(JSON.parse(raw) as Partial<NotifPrefs>) };
	} catch { /* private mode */ }
	return { ...NOTIF_DEFAULTS };
}

export function setNotifPrefs(p: Partial<NotifPrefs>): NotifPrefs {
	const next = { ...getNotifPrefs(), ...p };
	try { localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
	markPrefChanged(NOTIF_PREFS_KEY);
	window.dispatchEvent(new CustomEvent('tpt-notif-prefs-change', { detail: next }));
	return next;
}
/** 기본값으로 — 동기화가 '설정 없음' 을 반영할 때(다른 계정 기기 인수 등). 화면 갱신 이벤트는 set 과 동일 (2026-09-07) */
export function resetNotifPrefs(): NotifPrefs {
	try { localStorage.removeItem(NOTIF_PREFS_KEY); } catch { /* private mode */ }
	markPrefChanged(NOTIF_PREFS_KEY);
	const next = { ...NOTIF_DEFAULTS };
	window.dispatchEvent(new CustomEvent('tpt-notif-prefs-change', { detail: next }));
	return next;
}

// 현재 시각이 방해 금지 시간대인지 — 자정을 넘는 구간(22:00~08:00)도 처리.
export function isInDndWindow(now = new Date()): boolean {
	const p = getNotifPrefs();
	if (!p.dndEnabled) return false;
	const cur = now.getHours() * 60 + now.getMinutes();
	const [sh, sm] = p.dndStart.split(':').map(Number);
	const [eh, em] = p.dndEnd.split(':').map(Number);
	const start = sh * 60 + sm, end = eh * 60 + em;
	return start <= end ? (cur >= start && cur < end) : (cur >= start || cur < end);
}

// ── 파일 다운로드 후 폴더(탐색기) 자동 열기 (2026-08-20) — 데스크톱(Tauri) 전용 동작. 기본 켜짐(현행 유지).
//    끄면 저장만 하고 탐색기를 열지 않는다. 일괄 다운로드의 reveal=false(마지막 1회만) 규칙보다 우선해 전부 막는다.
const DOWNLOAD_REVEAL_KEY = 'tpt-download-reveal';
export function getDownloadReveal(): boolean {
	try { return localStorage.getItem(DOWNLOAD_REVEAL_KEY) !== '0'; } catch { return true; }
}
export function setDownloadReveal(on: boolean): void {
	try { localStorage.setItem(DOWNLOAD_REVEAL_KEY, on ? '1' : '0'); } catch { /* private mode */ }
}

// ── 🔄 자리를 비운 사이 자동 업데이트 (2026-09-17 사용자 "자동업데이트 여부를 개인설정에 넣게… 체크 되어 있을때만 동작하게(현재 정책대로)")
//    데스크톱(Tauri) 전용·기기별 저장. 기본 켜짐(현행 정책 = 5분 무입력이면 묻지 않고 설치). 끄면 UpdaterBanner 가 받아 두고 늘 [지금/나중에] 로 묻는다.
const AUTO_UPDATE_KEY = 'tpt-auto-update';
export function getAutoUpdate(): boolean {
	try { return localStorage.getItem(AUTO_UPDATE_KEY) !== '0'; } catch { return true; }
}
export function setAutoUpdate(on: boolean): void {
	try { localStorage.setItem(AUTO_UPDATE_KEY, on ? '1' : '0'); } catch { /* private mode */ }
}

// ── 📁 다운로드 저장 폴더 + 저장 위치 묻기 (2026-08-31 사용자) — 데스크톱(Tauri) 전용.
//    dir='' = OS 기본 Downloads 폴더. ask 기본 켜짐(현행 유지) — 켜면 저장 다이얼로그가 지정 폴더에서
//    열리고, 끄면 다이얼로그 없이 지정(없으면 기본) 폴더에 바로 저장한다. dedupe 는 Rust dl_begin 이 처리.
const DOWNLOAD_DIR_KEY = 'tpt-download-dir';
export function getDownloadDir(): string {
	try { return localStorage.getItem(DOWNLOAD_DIR_KEY) || ''; } catch { return ''; }
}
export function setDownloadDir(dir: string): void {
	try { localStorage.setItem(DOWNLOAD_DIR_KEY, dir); } catch { /* private mode */ }
}
const DOWNLOAD_ASK_KEY = 'tpt-download-ask';
export function getDownloadAsk(): boolean {
	try { return localStorage.getItem(DOWNLOAD_ASK_KEY) !== '0'; } catch { return true; }
}
export function setDownloadAsk(on: boolean): void {
	try { localStorage.setItem(DOWNLOAD_ASK_KEY, on ? '1' : '0'); } catch { /* private mode */ }
}

// ── 메시지 전송키 (2026-08-20) — 'enter'(기본): Enter 전송·Shift+Enter 줄바꿈 / 'shift-enter': 그 반대.
//    모바일(<768px)은 이 설정과 무관하게 Enter=줄바꿈, 전송은 보내기 버튼만 (2026-07-12 오전송 방지 유지).
// 🌐 제2 표시 언어 (2026-09-15 사용자 — "베트남 사람인데 한글을 쓰는 친구라, 조직도·대화에 한글 이름도 같이")
//   🔴 **등록 자체가 스위치**다 — 비워 두면(기본) 서버가 name2/display2 를 아예 담지 않아 화면이 종전과 똑같다.
//   값은 사람 이름 표기 언어에만 쓴다(UI 번역·메시지 번역과 무관). 빈 문자열 = 미등록.
const NAME_LANG2_KEY = 'tpt-name-lang2';
/** 선택 가능한 제2 언어 — 서버 NAME_LANGS 와 같은 집합 */
export const NAME_LANG2_OPTIONS = ['ko', 'en', 'vi', 'zh', 'es'] as const;
export type NameLang2 = '' | (typeof NAME_LANG2_OPTIONS)[number];
export function getNameLang2(): NameLang2 {
	try {
		const v = String(localStorage.getItem(NAME_LANG2_KEY) || '');
		return (NAME_LANG2_OPTIONS as readonly string[]).includes(v) ? v as NameLang2 : '';
	} catch { return ''; }
}
export function setNameLang2(v: NameLang2): void {
	try { if (v) localStorage.setItem(NAME_LANG2_KEY, v); else localStorage.removeItem(NAME_LANG2_KEY); } catch { /* private mode */ }
	markPrefChanged(NAME_LANG2_KEY);
	try { window.dispatchEvent(new CustomEvent('tpt:name-lang2-change')); } catch { /* */ }
}
/** 이름 병기 조립 — 판정 한 곳(회귀 테스트 대상). 2순위가 없거나 1순위와 같으면 **원래 이름 그대로**. */
export function nameWithAlt(name?: string | null, alt?: string | null): string {
	const a = String(name || '').trim();
	const b = String(alt || '').trim();
	if (!a) return b;                    // 1순위가 비면 2순위가 곧 이름
	if (!b || b === a) return a;
	return `${a} (${b})`;
}

const SEND_KEY_KEY = 'tpt-send-key';
export type SendKey = 'enter' | 'shift-enter';
export function getSendKey(): SendKey {
	try { return localStorage.getItem(SEND_KEY_KEY) === 'shift-enter' ? 'shift-enter' : 'enter'; } catch { return 'enter'; }
}
export function setSendKey(v: SendKey): void {
	try { localStorage.setItem(SEND_KEY_KEY, v); } catch { /* private mode */ }
	markPrefChanged(SEND_KEY_KEY);
}
/** 이 Enter 키 이벤트가 '전송' 인가 — 설정(enter/shift-enter)과 Shift 여부가 맞고 IME 조합 중이 아닐 때만.
 *  🔴 메시지를 보내는 입력창(대화·스레드·프로젝트 카드 댓글·Rich 에디터)은 전부 이 함수로 판정한다 — 한 곳만 하드코딩하면
 *  "답글·스레드에서는 Shift+Enter 설정이 안 먹는다"(2026-09-04 사용자) 가 재발한다. Ctrl/Meta+Enter 는 전송으로 보지 않는다. */
export function isSendKeyEvent(e: { key: string; shiftKey: boolean; ctrlKey?: boolean; metaKey?: boolean }, composing?: boolean): boolean {
	if (e.key !== 'Enter' || composing || e.ctrlKey || e.metaKey) return false;
	return e.shiftKey === (getSendKey() === 'shift-enter');
}

// ── 🗂 대화 목록 마지막 메시지 미리보기 (기본 off — 2026-07-06 사용자 지시). on 하면 미리보기+넓은 행.
//    2026-09-07 SettingsModal 인라인에서 이동 — 기기 간 동기화가 같은 setter 로 반영하게. 변경은 'tpt:show-last-message'(SidebarTree 수신).
const SHOW_LAST_MSG_KEY = 'tpt-show-last-message';
export function getShowLastMessage(): boolean {
	try { return localStorage.getItem(SHOW_LAST_MSG_KEY) === '1'; } catch { return false; }
}
export function setShowLastMessage(on: boolean): void {
	try { localStorage.setItem(SHOW_LAST_MSG_KEY, on ? '1' : '0'); } catch { /* private mode */ }
	markPrefChanged(SHOW_LAST_MSG_KEY);
	try { window.dispatchEvent(new CustomEvent('tpt:show-last-message', { detail: on })); } catch { /* */ }
}

// ── 🖼 댓글(스레드)의 사진·영상 표시 (2026-10-22 형 원격 "개인화 — 사진·영상을 댓글에 표시할지, 기본은 표시").
//    끄면 대화창의 댓글 말풍선·요약 줄에서 사진·영상을 인라인으로 그리지 않고 "📷/🎬 + 파일명" 한 줄(누르면 미리보기)로 줄인다.
//    우측 댓글 패널은 원문을 보는 곳이라 그대로 표시. 파일 카드는 영향 없음. 기기 간 동기화(prefsSync). 변경 = 'tpt:thread-media'.
const THREAD_MEDIA_KEY = 'tpt-thread-media';
export const THREAD_MEDIA_EVENT = 'tpt:thread-media';
export function getThreadMediaVisible(): boolean {
	try { return localStorage.getItem(THREAD_MEDIA_KEY) !== '0'; } catch { return true; }
}
export function setThreadMediaVisible(on: boolean): void {
	try { localStorage.setItem(THREAD_MEDIA_KEY, on ? '1' : '0'); } catch { /* private mode */ }
	markPrefChanged(THREAD_MEDIA_KEY);
	try { window.dispatchEvent(new CustomEvent(THREAD_MEDIA_EVENT, { detail: on })); } catch { /* */ }
}

// ── 📏 대화 목록 줄 간격 (2026-09-18 사용자 "대화방 목록의 간격도 글자크기처럼 조절, 모바일/데탑 별도") ──
//    글자 크기(--tpt-zoom)와 같은 결로 **사용자가 고르는 값**이되, 축은 '행 안쪽 위아래 여백에 더할 px' 이다.
//    0 = 종전 그대로(미리보기 켬 10px · 끔 4px). 음수면 더 촘촘하게, 양수면 더 널널하게.
//    🔴 모바일/데스크톱을 따로 담는다 — 같은 사람이라도 폰은 손가락, PC 는 마우스라 알맞은 간격이 다르다.
//       판정은 화면 폭(useIsMobile)이므로 PC 에서 창을 좁히면 모바일 값이 적용된다.
//    저장은 한 키(JSON)로 묶어 기기 간 동기화(prefsSync)에 그대로 실린다.
const ROOM_GAP_KEY = 'tpt-room-gap';
export const ROOM_GAP_CHANGE_EVENT = 'tpt:room-gap';
export const ROOM_GAP_MIN = -2;
export const ROOM_GAP_MAX = 10;
export interface RoomGap { m: number; d: number }
const ROOM_GAP_DEFAULT: RoomGap = { m: 0, d: 0 };

/** 범위 밖·NaN 은 0(종전 간격)으로 — 저장값이 깨져도 목록이 무너지지 않게. */
export function clampRoomGap(n: unknown): number {
	const v = typeof n === 'number' ? n : Number(n);
	if (!Number.isFinite(v)) return 0;
	return Math.min(ROOM_GAP_MAX, Math.max(ROOM_GAP_MIN, Math.round(v)));
}

export function getRoomGap(): RoomGap {
	try {
		const raw = localStorage.getItem(ROOM_GAP_KEY);
		if (raw) {
			const j = JSON.parse(raw) as Partial<RoomGap>;
			return { m: clampRoomGap(j.m), d: clampRoomGap(j.d) };
		}
	} catch { /* private mode·깨진 값 */ }
	return { ...ROOM_GAP_DEFAULT };
}

export function setRoomGap(patch: Partial<RoomGap>): RoomGap {
	const next: RoomGap = { ...getRoomGap() };
	if (patch.m !== undefined) next.m = clampRoomGap(patch.m);
	if (patch.d !== undefined) next.d = clampRoomGap(patch.d);
	try {
		if (next.m === 0 && next.d === 0) localStorage.removeItem(ROOM_GAP_KEY);   // 기본값은 저장하지 않는다(동기화 seed 방지)
		else localStorage.setItem(ROOM_GAP_KEY, JSON.stringify(next));
	} catch { /* private mode */ }
	markPrefChanged(ROOM_GAP_KEY);
	try { window.dispatchEvent(new CustomEvent(ROOM_GAP_CHANGE_EVENT, { detail: next })); } catch { /* SSR */ }
	return next;
}

/** 동기화가 '설정 없음' 을 반영할 때 — 기본 간격으로 되돌린다(화면 갱신 이벤트는 set 과 동일). */
export function resetRoomGap(): RoomGap {
	try { localStorage.removeItem(ROOM_GAP_KEY); } catch { /* private mode */ }
	markPrefChanged(ROOM_GAP_KEY);
	const next = { ...ROOM_GAP_DEFAULT };
	try { window.dispatchEvent(new CustomEvent(ROOM_GAP_CHANGE_EVENT, { detail: next })); } catch { /* SSR */ }
	return next;
}

// ── 📮 VOC(문의·개선 요청) ? 버튼 표시 (2026-08-24 원격 요청) — 기본 켜짐.
//    꺼도 기능은 그대로 쓸 수 있다: 하단 [더보기]·데스크톱 프로필 메뉴 진입점은 남는다(대화 화면의 떠 있는
//    버튼만 감춘다). 변경은 'tpt:voc-fab' 이벤트로 즉시 반영(ChatPage 수신).
const VOC_FAB_KEY = 'tpt-voc-fab';
export function getVocFabVisible(): boolean {
	try { return localStorage.getItem(VOC_FAB_KEY) !== '0'; } catch { return true; }
}
export function setVocFabVisible(on: boolean): void {
	try { localStorage.setItem(VOC_FAB_KEY, on ? '1' : '0'); } catch { /* private mode */ }
	markPrefChanged(VOC_FAB_KEY);
	try { window.dispatchEvent(new CustomEvent('tpt:voc-fab', { detail: on })); } catch { /* */ }
}

// ── 💬 입력창 아래 도구 칩 줄(자동번역·AI 답글추천·AI 작성·맞춤법·명령) 표시 (2026-08-31 원격
//    "대화방을 좀 크게 보려는 사람이 있어서") — 기본 켜짐. 꺼도 기능은 남는다: 자동번역은 방 헤더 🌐,
//    명령은 '/' 입력으로 접근 가능. 변경은 이벤트로 즉시 반영(ComposerFooter 수신). ──
const COMPOSER_CHIPS_KEY = 'tpt-composer-chips';
export const COMPOSER_CHIPS_CHANGE_EVENT = 'tpt:composer-chips';
export function getComposerChipsVisible(): boolean {
	try { return localStorage.getItem(COMPOSER_CHIPS_KEY) !== '0'; } catch { return true; }
}
export function setComposerChipsVisible(on: boolean): void {
	try { localStorage.setItem(COMPOSER_CHIPS_KEY, on ? '1' : '0'); } catch { /* private mode */ }
	markPrefChanged(COMPOSER_CHIPS_KEY);
	try { window.dispatchEvent(new CustomEvent(COMPOSER_CHIPS_CHANGE_EVENT, { detail: on })); } catch { /* */ }
}

// ── ✍ 서식 입력창 (2026-10-24 형 A안 "서식이 입력창에 바로 보이게") — 'rich'(기본) | 'plain'(종전 입력창).
//    🔴 기기별 설정 — SYNCED_PREFS 에 넣지 않는다(PC 마다 한글 입력기가 달라, 문제가 생긴 기기만 끌 수 있게 — 형 결정).
//    실제로 켜지는 조건(기능 스위치·PC 화면)은 messageview/composer/useRichComposerMode 한 곳. ──
const COMPOSER_MODE_KEY = 'tpt-composer-mode';
export const COMPOSER_MODE_CHANGE_EVENT = 'tpt:composer-mode';
export type ComposerMode = 'rich' | 'plain';
export function getComposerMode(): ComposerMode {
	try { return localStorage.getItem(COMPOSER_MODE_KEY) === 'plain' ? 'plain' : 'rich'; } catch { return 'rich'; }
}
export function setComposerMode(mode: ComposerMode): void {
	try { localStorage.setItem(COMPOSER_MODE_KEY, mode); } catch { /* private mode */ }
	try { window.dispatchEvent(new CustomEvent(COMPOSER_MODE_CHANGE_EVENT, { detail: mode })); } catch { /* */ }
}

// ── ⌨️ 방 진입 시 입력창 자동 포커스 (2026-10-29 형 "토글로 — 기본은 현재처럼 입력모드") — 기본 켜짐.
//    기기별(서식 입력창과 같은 결) — SYNCED_PREFS 에 넣지 않는다. 적용 지점은 messageview/hooks/useComposer 방 진입 effect 한 곳. ──
const COMPOSER_AUTOFOCUS_KEY = 'tpt-composer-autofocus';
export function getComposerAutoFocus(): boolean {
	try { return localStorage.getItem(COMPOSER_AUTOFOCUS_KEY) !== '0'; } catch { return true; }
}
export function setComposerAutoFocus(on: boolean): void {
	try { localStorage.setItem(COMPOSER_AUTOFOCUS_KEY, on ? '1' : '0'); } catch { /* private mode */ }
}

// ── 📱 모바일 입력창 안 버튼(🎤 마이크·😊 이모티콘) 표시 (2026-11-02 형 "숨김처리 — 기본은 표시, 입력창을 크게") — 기본 켜짐.
//    모바일 화면에만 적용(ComposerFooter) — 기기별, SYNCED_PREFS 에 넣지 않는다(PC 화면과 무관). 변경은 이벤트로 즉시 반영. ──
const COMPOSER_MIC_KEY = 'tpt-composer-mic';
const COMPOSER_EMOJI_KEY = 'tpt-composer-emoji';
export const COMPOSER_BTNS_CHANGE_EVENT = 'tpt:composer-btns';
export function getComposerMicVisible(): boolean {
	try { return localStorage.getItem(COMPOSER_MIC_KEY) !== '0'; } catch { return true; }
}
export function getComposerEmojiVisible(): boolean {
	try { return localStorage.getItem(COMPOSER_EMOJI_KEY) !== '0'; } catch { return true; }
}
export function setComposerBtnVisible(which: 'mic' | 'emoji', on: boolean): void {
	try { localStorage.setItem(which === 'mic' ? COMPOSER_MIC_KEY : COMPOSER_EMOJI_KEY, on ? '1' : '0'); } catch { /* private mode */ }
	try { window.dispatchEvent(new CustomEvent(COMPOSER_BTNS_CHANGE_EVENT)); } catch { /* */ }
}

// ── 🔴 상단 탭 안읽음 배지 (2026-09-03 원격 "안읽은 대화개수를 pc,web버전에서 제일 상단에 텝메뉴에
//    개수를 표시… 토글로 표시여부를 개인이 결정토록(기본은 해제)") — 기본 꺼짐.
//    판정은 LeftNavbar 한 곳에서만 한다(모든 페이지가 그 컴포넌트를 쓰므로 화면마다 조건이 어긋날 일이 없다). ──
const NAV_UNREAD_BADGE_KEY = 'tpt-nav-unread-badge';
export const NAV_UNREAD_BADGE_CHANGE_EVENT = 'tpt:nav-unread-badge';
export function getNavUnreadBadge(): boolean {
	try { return localStorage.getItem(NAV_UNREAD_BADGE_KEY) === '1'; } catch { return false; }
}
export function setNavUnreadBadge(on: boolean): void {
	try { localStorage.setItem(NAV_UNREAD_BADGE_KEY, on ? '1' : '0'); } catch { /* private mode */ }
	markPrefChanged(NAV_UNREAD_BADGE_KEY);
	try { window.dispatchEvent(new CustomEvent(NAV_UNREAD_BADGE_CHANGE_EVENT, { detail: on })); } catch { /* */ }
}

// ── 🔢 앱 아이콘/작업 표시줄 숫자 배지 (2026-09-18 설문 "카톡 1 떠 있는 걸 못 참는데 끌 방법이 없다") ──
//    기본 켜짐(종전 동작). 끄면 desktop/badge 로 0 을 보내 OS 배지를 지운다 — 방 행·탭 배지는 그대로.
const APP_BADGE_KEY = 'tpt-app-badge';
export const APP_BADGE_CHANGE_EVENT = 'tpt:app-badge';
export function getAppBadge(): boolean {
	try { return localStorage.getItem(APP_BADGE_KEY) !== '0'; } catch { return true; }
}
export function setAppBadge(on: boolean): void {
	try { localStorage.setItem(APP_BADGE_KEY, on ? '1' : '0'); } catch { /* private mode */ }
	markPrefChanged(APP_BADGE_KEY);
	try { window.dispatchEvent(new CustomEvent(APP_BADGE_CHANGE_EVENT, { detail: on })); } catch { /* */ }
}

// ── 🖼 파일함 보기 모드 (2026-09-18 설문 "사진이 많아지면 썸네일로 모아보기가 있어야 한다 · 3개월 전 사진을
//    파일명만 보고 찾기는 매우 어렵다") — 'list'(기본, 종전 동작) / 'grid'(정사각 썸네일 격자).
//    데스크톱·모바일 파일함이 같은 키를 쓴다(같은 사람의 같은 취향). 화면 배치가 아니라 '무엇을 보느냐' 라
//    기기 특성으로 보지 않았지만, 동기화 등록은 하지 않았다 — prefsSync 반영은 별도 판단.
const FILES_VIEW_KEY = 'tpt-files-view';
export type FilesView = 'list' | 'grid';
export function getFilesView(): FilesView {
	try { return localStorage.getItem(FILES_VIEW_KEY) === 'grid' ? 'grid' : 'list'; } catch { return 'list'; }
}
export function setFilesView(v: FilesView): void {
	try {
		if (v === 'grid') localStorage.setItem(FILES_VIEW_KEY, 'grid');
		else localStorage.removeItem(FILES_VIEW_KEY);   // 기본값은 저장하지 않는다
	} catch { /* private mode */ }
}

// 안읽음 총합 공유 — 대화 화면(ChatPage)만 이 값을 계산할 수 있어서(방 목록·구독을 그 페이지가 들고 있다),
//   계산된 값을 여기 얹어 드라이브·프로젝트·일정 화면의 상단 탭도 같은 숫자를 쓴다.
//   🔴 폴링을 새로 만들지 않는다 — 그 화면들에 구독 폴링을 붙이면 요청이 화면 수만큼 늘어난다.
//   대화 화면을 거치면 갱신되고, 그 뒤 다른 화면에서는 마지막 값이 보인다(읽음 처리도 대화 화면에서 일어난다).
const UNREAD_TOTAL_KEY = 'tpt-unread-total';
export const UNREAD_TOTAL_CHANGE_EVENT = 'tpt:unread-total';
export function getCachedUnreadTotal(): number {
	try { const n = parseInt(localStorage.getItem(UNREAD_TOTAL_KEY) || '0', 10); return Number.isFinite(n) && n > 0 ? n : 0; } catch { return 0; }
}
export function setCachedUnreadTotal(n: number): void {
	const v = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
	try { localStorage.setItem(UNREAD_TOTAL_KEY, String(v)); } catch { /* private mode */ }
	try { window.dispatchEvent(new CustomEvent(UNREAD_TOTAL_CHANGE_EVENT, { detail: v })); } catch { /* */ }
}
